import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import financeApiService from "../../../../services/financeApiService";
import tools from "../../../../helpers/tools";
import { messageErreurDepot } from "../../../../helpers/depotTools";
import ConfirmModal from "../../../common/ConfirmModal/ConfirmModal";
import styles from "../FinanceDetailV2.module.scss";

// LS-672 — généré automatiquement (LS-587) : absent de `typeOptions` (non
// sélectionnable à l'upload manuel, cf. TypeDocument::estSelectionnableAUpload)
// mais doit rester visible en lecture, sans option de suppression manuelle.
const TABLEAU_RECAPITULATIF_TYPE = "tableau_recapitulatif";
const TABLEAU_RECAPITULATIF_LABEL = "Tableau récapitulatif";

const MIME_ICONS = {
  "application/pdf": "file-pdf-o",
  "image/jpeg": "file-image-o",
  "image/png": "file-image-o",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    "file-excel-o",
  "application/vnd.ms-excel": "file-excel-o",
};

function formatSize(bytes) {
  if (!bytes) return "";
  const units = ["o", "Ko", "Mo", "Go"];
  let value = bytes;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${value.toFixed(unitIndex > 0 && value < 10 ? 1 : 0)} ${units[unitIndex]}`;
}

/**
 * Dépôt et consultation des documents libres d'une demande de financement.
 * Le dépôt passe par une modale qui impose de qualifier le document via la
 * liste fermée des catégories avant l'upload. Cette liste (`typeOptions`)
 * est décidée côté API (constante `FINANCEMENT.TYPES_DOCUMENT_UPLOAD_MANUEL`,
 * dérivée de l'enum `TypeDocument`) : un ajout de catégorie côté API se
 * répercute ici sans changement front. Le droit de dépôt est décidé par
 * l'appelant (`disabled`) : il suit les mêmes règles de verrouillage que
 * l'édition de la demande, avec un accès étendu pour les rôles à privilèges.
 *
 * LS-638 — certains types (`requiertUnDevis`) imposent de rattacher le
 * document à un ou plusieurs devis de la demande (`devisOptions`, `autoriseMultiDevis`
 * pour le nombre autorisé) : ces deux règles viennent de l'API, jamais
 * hardcodées ici. L'upload multi-devis reste un même fichier envoyé une fois
 * par devis sélectionné — l'endpoint ne connaît qu'un `devis_uuid` singulier.
 *
 * LS-655 — le sélecteur de devis n'est plus réservé aux types qui l'imposent :
 * il s'affiche pour tout type dès que le dossier a au moins un devis, en
 * facultatif (pas d'astérisque, ne bloque pas la confirmation). Dans les deux
 * cas (obligatoire ou facultatif), l'unique devis du dossier est pré-sélectionné
 * automatiquement s'il n'y en a qu'un.
 *
 * LS-648 — la liste agrège tous les types via un appel par type, plutôt qu'un
 * seul type par défaut : sinon les documents uploadés dans un type autre que
 * "autre" disparaissaient de la liste au rechargement.
 *
 * ST1-2069 — les types rattachés à un devis (CPI, calendrier des loyers) sont
 * listés ici aussi, et plus seulement dans l'onglet du devis concerné
 * (EcheancierUploader) : la checklist est placée à côté de cette section
 * (LS-646) précisément pour vérifier que les documents attendus sont déposés,
 * elle doit donc les voir tous. Le devis rattaché est rappelé par sa
 * référence. Les deux listes restent synchronisées par le parent : `onChanged`
 * signale un dépôt ou une suppression ici, `reloadKey` force un rechargement
 * quand l'onglet du devis a déposé un document.
 *
 * LS-649 — suppression définitive d'un document. Le droit de suppression
 * (`canDelete`) suit la même règle de verrouillage que l'édition de la
 * demande (contrairement à l'upload, toujours autorisé) ; le serveur reste
 * la source de vérité en cas de blocage (422).
 */
const DocumentsUploader = ({
  uuid,
  disabled = false,
  canDelete = true,
  typeOptions = [],
  devisOptions = [],
  reloadKey = 0,
  onChanged,
}) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDevisUuids, setSelectedDevisUuids] = useState([]);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const selectedTypeOption = typeOptions.find(
    (opt) => opt.value === selectedType,
  );
  const requiertUnDevis = !!selectedTypeOption?.requiertUnDevis;
  const autoriseMultiDevis = !!selectedTypeOption?.autoriseMultiDevis;

  function devisPreselectionnes() {
    return devisOptions.length === 1 ? [devisOptions[0].value] : [];
  }

  useEffect(() => {
    if (!uuid) return;
    setIsLoading(true);
    Promise.all([
      ...typeOptions.map((opt) => financeApiService.getDocuments(uuid, opt.value)),
      // LS-672 — 404 tant qu'aucun tableau n'a encore été généré.
      financeApiService.getRecap(uuid).catch(() => ({ data: null })),
    ])
      .then((responses) =>
        setDocuments(responses.flatMap((res) => res.data || [])),
      )
      .catch(() => toast.error("Impossible de charger les documents."))
      .finally(() => setIsLoading(false));
    // typeOptions volontairement omis : sa référence change à chaque render du
    // parent (FinanceDetailV2) alors que son contenu est stable pour la durée
    // de la session (issu des constantes API) — l'inclure provoquerait un
    // rechargement de la liste à chaque re-render du parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, reloadKey]);

  function openDeleteConfirm(doc) {
    setDocumentToDelete(doc);
  }

  function closeDeleteConfirm() {
    if (isDeleting) return;
    setDocumentToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!documentToDelete) return;

    const supprime = documentToDelete;
    setIsDeleting(true);
    try {
      await financeApiService.supprimerDocument(
        uuid,
        supprime.type,
        supprime.uuid,
      );
      setDocuments((prev) => prev.filter((doc) => doc.uuid !== supprime.uuid));
      toast.success("Document supprimé.");
      setDocumentToDelete(null);
      if (onChanged) onChanged({ action: "delete", type: supprime.type });
    } catch (err) {
      const message =
        err.response?.status === 422
          ? "Cette demande est verrouillée, le document ne peut plus être supprimé."
          : err.response?.data?.message ||
            "La suppression du document a échoué.";
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  }

  function openModal() {
    setSelectedType("");
    setSelectedFile(null);
    setSelectedDevisUuids(devisPreselectionnes());
    setIsModalOpen(true);
  }

  function closeModal() {
    if (isUploading) return;
    setIsModalOpen(false);
  }

  function handleTypeChange(value) {
    setSelectedType(value);
    setSelectedDevisUuids(devisPreselectionnes());
  }

  function toggleDevis(devisUuid) {
    setSelectedDevisUuids((prev) =>
      prev.includes(devisUuid)
        ? prev.filter((uuid) => uuid !== devisUuid)
        : [...prev, devisUuid],
    );
  }

  async function handleConfirmUpload() {
    if (!selectedType || !selectedFile) return;
    if (requiertUnDevis && selectedDevisUuids.length === 0) return;

    setIsUploading(true);
    try {
      const devisUuids = selectedDevisUuids.length > 0 ? selectedDevisUuids : [null];
      const uploaded = [];
      for (const devisUuid of devisUuids) {
        const res = devisUuid
          ? await financeApiService.uploaderDocument(
              uuid,
              selectedFile,
              selectedType,
              devisUuid,
            )
          : await financeApiService.uploaderDocument(
              uuid,
              selectedFile,
              selectedType,
            );
        uploaded.push(res.data);
      }
      setDocuments((prev) => [...prev, ...uploaded]);
      toast.success(
        uploaded.length > 1 ? "Documents déposés." : "Document déposé.",
      );
      setIsModalOpen(false);
      if (onChanged) onChanged({ action: "upload", type: selectedType });
    } catch (err) {
      toast.error(
        messageErreurDepot(err, {
          interdit:
            "Vous n'êtes pas autorisé à déposer un document sur cette demande.",
          defaut: "L'upload du document a échoué.",
        }),
      );
    } finally {
      setIsUploading(false);
    }
  }

  if (!uuid) {
    return (
      <div className={styles.financierCardStatusMuted}>
        Enregistrez la demande pour pouvoir déposer des documents.
      </div>
    );
  }

  return (
    <div className={styles.documentsSection}>
      {!disabled && (
        <div className={styles.documentsUploadRow}>
          <button type="button" className={styles.btnSave} onClick={openModal}>
            <i className="fa fa-upload" style={{ marginRight: 4 }} />
            Déposer un document
          </button>
        </div>
      )}

      {isLoading ? (
        <div className={styles.financierCardStatusMuted}>Chargement…</div>
      ) : documents.length === 0 ? (
        <div className={styles.financierCardStatusMuted}>
          Aucun document déposé.
        </div>
      ) : (
        <ul className={styles.documentsList}>
          {documents.map((doc) => {
            const typeLabel =
              doc.type === TABLEAU_RECAPITULATIF_TYPE
                ? TABLEAU_RECAPITULATIF_LABEL
                : typeOptions.find((opt) => opt.value === doc.type)?.label;
            // ST1-2069 — devis rattaché (obligatoire pour CPI/calendrier, facultatif
            // sinon, LS-655) rappelé par sa référence ; rien si le devis a depuis
            // été retiré de la demande.
            const devisLabel = doc.devisUuid
              ? devisOptions.find((opt) => opt.value === doc.devisUuid)?.label
              : null;
            return (
              <li key={doc.uuid} className={styles.documentItem}>
                <i
                  className={`fa fa-${MIME_ICONS[doc.mimeType] || "file-o"} ${styles.documentIcon}`}
                />
                <span className={styles.documentBody}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.documentName}
                  >
                    {typeLabel ? `${typeLabel} — ${doc.fileName}` : doc.fileName}
                  </a>
                  <span className={styles.documentMeta}>
                    {devisLabel ? `Devis ${devisLabel} · ` : ""}
                    {/* espaces insécables : taille et date restent sur la même ligne */}
                    {formatSize(doc.size)}
                    {doc.createdAt ? `\u00A0·\u00A0${tools.formatDate(doc.createdAt)}` : ""}
                  </span>
                </span>
                {canDelete && doc.type !== TABLEAU_RECAPITULATIF_TYPE && (
                  <button
                    type="button"
                    className={styles.documentDeleteBtn}
                    title="Supprimer ce document"
                    onClick={() => openDeleteConfirm(doc)}
                  >
                    <i className="fa fa-trash" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {isModalOpen && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={closeModal}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Déposer un document</h5>
                <button type="button" className="close" onClick={closeModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="document-type">Type de document *</label>
                  <select
                    id="document-type"
                    className="form-control"
                    value={selectedType}
                    onChange={(e) => handleTypeChange(e.target.value)}
                  >
                    <option value="">Sélectionnez un type</option>
                    {typeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {!!selectedType && devisOptions.length > 0 && (
                  <div className="form-group" data-testid="devis-picker">
                    <label>
                      Devis {autoriseMultiDevis ? "concernés" : "concerné"}
                      {requiertUnDevis ? " *" : " (optionnel)"}
                    </label>
                    {autoriseMultiDevis ? (
                      <div className={styles.devisCheckboxList}>
                        {devisOptions.map((opt) => (
                          <label key={opt.value} className={styles.devisCheckboxItem}>
                            <input
                              type="checkbox"
                              checked={selectedDevisUuids.includes(opt.value)}
                              onChange={() => toggleDevis(opt.value)}
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <select
                        className="form-control"
                        value={selectedDevisUuids[0] || ""}
                        onChange={(e) =>
                          setSelectedDevisUuids(
                            e.target.value ? [e.target.value] : [],
                          )
                        }
                      >
                        <option value="">Sélectionnez un devis</option>
                        {devisOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className="form-group mb-0">
                  <label htmlFor="document-file">Fichier *</label>
                  <input
                    id="document-file"
                    type="file"
                    className="form-control-file"
                    accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls"
                    onChange={(e) =>
                      setSelectedFile(e.target.files?.[0] || null)
                    }
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={closeModal}
                  disabled={isUploading}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirmUpload}
                  disabled={
                    isUploading ||
                    !selectedType ||
                    !selectedFile ||
                    (requiertUnDevis && selectedDevisUuids.length === 0)
                  }
                >
                  <i
                    className={`fa fa-${isUploading ? "spinner fa-spin" : "upload"}`}
                    style={{ marginRight: 4 }}
                  />
                  {isUploading ? "Envoi..." : "Déposer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!documentToDelete}
        title="Supprimer le document"
        message={`Voulez-vous vraiment supprimer « ${documentToDelete?.fileName} » ? Cette action est définitive.`}
        confirmLabel="Supprimer"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteConfirm}
      />
    </div>
  );
};

export default DocumentsUploader;
